import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { MoreVertical, type LucideIcon } from "lucide-react";
import { cn } from "@hey404/ui";

export interface RowMenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  /** Destructive actions sit last, after a divider, and turn ink on hover. */
  danger?: boolean;
}

/**
 * The ⋮ button at the end of a list row, with that row's actions in a small
 * menu. Rendered in a portal with fixed positioning so a card or table never
 * clips it; opens upwards near the bottom of the screen. Keyboard: Enter,
 * Space or ↓ open it, arrows move, Home/End jump, Escape and Tab close.
 */
export function RowMenu({
  label,
  items,
  ghost,
}: {
  /** Accessible name, e.g. "Acciones para Camila Ruiz". */
  label: string;
  items: RowMenuItem[];
  /** Borderless and smaller, for use beside a comment bubble. */
  ghost?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<{
    right: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const place = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const up = window.innerHeight - rect.bottom < 200;
    setPosition({
      right: window.innerWidth - rect.right,
      ...(up
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
  };
  const show = (index = 0) => {
    place();
    setActive(index);
    setOpen(true);
  };
  const close = (focusButton = true) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  };

  useLayoutEffect(() => {
    if (open)
      menuRef.current
        ?.querySelectorAll<HTMLButtonElement>("[role=menuitem]")
        [active]?.focus();
  }, [open, active]);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      )
        setOpen(false);
    };
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  const onMenuKey = (event: KeyboardEvent) => {
    const last = items.length - 1;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (index === last ? 0 : index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (index === 0 ? last : index - 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(last);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        title="Acciones"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close() : show())}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            show(event.key === "ArrowUp" ? items.length - 1 : 0);
          }
        }}
        className={cn(
          "rounded-control grid cursor-pointer place-items-center transition-colors",
          ghost ? "h-8 w-8" : "border-ink border-chip h-9 w-9",
          open
            ? "bg-ink text-paper"
            : ghost
              ? "text-ink-soft hover:bg-paper-dim hover:text-ink"
              : "bg-paper hover:bg-paper-dim",
        )}
      >
        <MoreVertical size={16} aria-hidden="true" />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            onKeyDown={onMenuKey}
            style={{
              position: "fixed",
              right: position.right,
              top: position.top,
              bottom: position.bottom,
            }}
            className="border-ink bg-paper border-chip rounded-control shadow-brutal z-[60] min-w-52 p-1"
          >
            {items.map((item, index) => {
              const Icon = item.icon;
              const divider =
                item.danger && index > 0 && !items[index - 1]?.danger;
              return (
                <div key={item.label}>
                  {divider && (
                    <div
                      aria-hidden="true"
                      className="border-ink/15 my-1 border-t"
                    />
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={index === active ? 0 : -1}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => {
                      close(false);
                      item.onSelect();
                    }}
                    className={cn(
                      "flex min-h-10 w-full cursor-pointer items-center gap-2.5 rounded-[4px] px-3 py-2 text-left text-sm outline-none",
                      item.danger
                        ? "focus:bg-ink focus:text-paper"
                        : "focus:bg-lemon/45",
                    )}
                  >
                    <Icon size={15} aria-hidden="true" className="shrink-0" />
                    {item.label}
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
